import { useCallback, useRef } from 'react'
import {
  DEFAULT_GRADE,
  DEFAULT_GRADE_DURATION_S,
  newId,
  type ColorGrade,
  type ToolProject,
} from '../model'
import type { VirtualTimeline } from '../timeline'

export interface ColorGradesOptions {
  onChange: (updater: (project: ToolProject) => ToolProject) => void
  grades: ColorGrade[]
  timeline: VirtualTimeline
  playhead: number
  seekTo: (time: number) => void
  pause: () => void
  onSelect: (id: string | null) => void
}

export interface ColorGradesApi {
  addGrade: () => void
  patchGrade: (id: string, patch: Partial<ColorGrade>) => void
  deleteGrade: (id: string) => void
  duplicateGrade: (id: string) => void
  selectGradeAndSeek: (id: string | null) => void
}

/**
 * Writes to ``project.grades`` — colour correction over a stretch of the
 * cut. Deliberately the same shape as {@link useTextOverlays}: both are
 * time-ranged tags on the same strip, and behaving differently would be a
 * difference the user has to learn for no reason.
 */
export function useColorGrades({
  onChange,
  grades,
  timeline,
  playhead,
  seekTo,
  pause,
  onSelect,
}: ColorGradesOptions): ColorGradesApi {
  const playheadRef = useRef(playhead)
  playheadRef.current = playhead
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  const gradesRef = useRef(grades)
  gradesRef.current = grades

  const addGrade = useCallback(() => {
    const id = newId('grd')
    const start = playheadRef.current
    const total = timelineRef.current.duration
    onChange((p) => ({
      ...p,
      grades: [
        ...p.grades,
        {
          ...DEFAULT_GRADE,
          id,
          start,
          end: Math.min(total, start + DEFAULT_GRADE_DURATION_S),
        },
      ],
    }))
    onSelect(id)
  }, [onChange, onSelect])

  const patchGrade = useCallback(
    (id: string, patch: Partial<ColorGrade>) => {
      onChange((p) => ({
        ...p,
        grades: p.grades.map((g) => (g.id === id ? { ...g, ...patch } : g)),
      }))
    },
    [onChange],
  )

  const deleteGrade = useCallback(
    (id: string) => {
      onChange((p) => ({ ...p, grades: p.grades.filter((g) => g.id !== id) }))
      onSelect(null)
    },
    [onChange, onSelect],
  )

  const duplicateGrade = useCallback(
    (id: string) => {
      const source = gradesRef.current.find((g) => g.id === id)
      if (!source) return
      const total = timelineRef.current.duration
      const length = source.end - source.start
      const start = source.end + length <= total ? source.end : Math.max(0, total - length)
      const copy: ColorGrade = { ...source, id: newId('grd'), start, end: Math.min(total, start + length) }
      onChange((p) => ({ ...p, grades: [...p.grades, copy] }))
      onSelect(copy.id)
      seekTo(copy.start)
    },
    [onChange, onSelect, seekTo],
  )

  const selectGradeAndSeek = useCallback(
    (id: string | null) => {
      onSelect(id)
      if (!id) return
      const target = gradesRef.current.find((g) => g.id === id)
      if (!target) return
      pause()
      // Same nudge the captions use: land *inside* the range, not on the
      // boundary frame the previous tag also owns.
      seekTo(target.start + 0.05)
    },
    [onSelect, pause, seekTo],
  )

  return { addGrade, patchGrade, deleteGrade, duplicateGrade, selectGradeAndSeek }
}
